import {IntlShape} from 'react-intl'

import {PriceHistoryDataPoint} from '../services/priceInsightsApi'
import {CHART_COLORS} from '../utils/constants'
import {priceTrendMessages} from '../utils/messages'

interface DatasetConfig {
  label: string
  data: number[]
  borderColor: string
  backgroundColor: string
  borderWidth: number
  tension: number
  pointRadius: number
  pointHoverRadius: number
}

/**
 * Creates a standardized dataset configuration for Chart.js
 */
const createDatasetConfig = (
  label: string,
  data: number[],
  isPrimary: boolean,
  primaryColor: string
): DatasetConfig => ({
  label,
  data,
  borderColor: isPrimary ? primaryColor : CHART_COLORS.secondary,
  backgroundColor: isPrimary ? primaryColor : CHART_COLORS.secondary,
  borderWidth: 3,
  tension: 0.4, // Smooth curves
  pointRadius: 0, // Hide default dots, custom plugin draws them
  pointHoverRadius: 0
})

/**
 * Builds datasets for the price history chart.
 * @param primaryColor - Resolved brand color hex (from getCssVariableValue). Falls back to CHART_COLORS.primary.
 */
export const buildChartDatasets = (
  hotelPrices: number[],
  similarHotelsPrices: number[] | undefined,
  formatMessage: IntlShape['formatMessage'],
  primaryColor: string = CHART_COLORS.primary
): DatasetConfig[] => {
  const datasets: DatasetConfig[] = [
    createDatasetConfig(
      formatMessage(priceTrendMessages.priceHistoryThisHotel),
      hotelPrices,
      true, // isPrimary
      primaryColor
    )
  ]

  // Add similar hotels dataset if available
  if (similarHotelsPrices && similarHotelsPrices.length > 0) {
    datasets.push(
      createDatasetConfig(
        formatMessage(priceTrendMessages.priceHistorySimilarHotels),
        similarHotelsPrices,
        false, // isPrimary
        primaryColor
      )
    )
  }

  return datasets
}

/**
 * Y-axis configuration defaults for the chart.
 * Adjust here to tune the visual density of ticks.
 */
const Y_AXIS_CONFIG = {
  desiredTicks: 4,
  ticksPaddingBelow: 1,
  ticksPaddingAbove: 1,
  niceSteps: [5, 10, 20, 25, 50, 100, 200, 500, 1000],
  /**
   * Minimum visual range as a percentage of average price.
   * Prevents small price fluctuations from appearing dramatic on the chart.
   * E.g., 0.10 means the Y-axis will show at least 10% of the average price range.
   */
  minRangePercentage: 0.1
} as const

/**
 * Returns labels and prices, adding a new "now" point with offerPrice when provided.
 * Uses nightlyPrice by default, or price (total) when showTotalPrices is true.
 * Note: History data is ordered most-recent-first, so index 0 is the newest historical data point.
 * When offerPrice is provided, adds a new point at "now" (index 0) with the current price,
 * preserving all historical data points. This ensures the chart shows both historical data
 * and the current offer price accurately positioned at "now".
 */
export const buildLabelsAndHotelPrices = (
  hotelHistory: PriceHistoryDataPoint[],
  offerPrice: number | undefined,
  showTotalPrices: boolean
) => {
  const labels = hotelHistory.map(point => point.timestamp)
  const hotelPrices = hotelHistory.map(point =>
    showTotalPrices ? point.price : point.nightlyPrice
  )

  if (offerPrice !== undefined) {
    // Add a new point at "now" with the current offer price
    // This preserves all historical data points and shows current price at the correct position
    labels.unshift(new Date().toISOString())
    hotelPrices.unshift(offerPrice)
  }

  return {labels, hotelPrices}
}

/**
 * Adds a "now" point to similar hotels prices to match the hotel history data.
 * When hotel history has a "now" point (from offerPrice), similar hotels should also
 * have a corresponding point at "now" to avoid visual gaps in the chart.
 * Uses the last (most recent) similar hotels price value.
 */
export const addNowPointToSimilarHotels = (
  similarHotelsPrices: number[] | undefined,
  hasNowPoint: boolean
): number[] | undefined => {
  if (!similarHotelsPrices || similarHotelsPrices.length === 0) {
    return similarHotelsPrices
  }

  if (!hasNowPoint) {
    return similarHotelsPrices
  }

  // Copy the last (most recent) price and add it at the beginning for "now"
  // This matches the structure where hotel history has "now" as index 0
  const lastPrice = similarHotelsPrices[0] // Most recent is first in the array
  return [lastPrice, ...similarHotelsPrices]
}

/**
 * Computes y-axis bounds and step size based on provided price data.
 * Ensures a minimum visual range to prevent small fluctuations from looking dramatic.
 */
export const computeYAxisConfig = (
  hotelPrices: number[],
  similarPrices: number[] | undefined
) => {
  const {
    desiredTicks,
    ticksPaddingAbove,
    ticksPaddingBelow,
    niceSteps,
    minRangePercentage
  } = Y_AXIS_CONFIG

  const allPrices = [...hotelPrices, ...(similarPrices || [])]
  const minPrice = Math.min(...allPrices)
  const maxPrice = Math.max(...allPrices)
  const avgPrice = allPrices.reduce((a, b) => a + b, 0) / allPrices.length

  // Ensure minimum visual range based on average price
  // This prevents small price changes from appearing dramatic on the chart
  const minVisualRange = avgPrice * minRangePercentage
  const actualRange = maxPrice - minPrice
  const range = Math.max(actualRange, minVisualRange, 1)

  const roughStep = Math.ceil(
    range / Math.max(desiredTicks - ticksPaddingBelow - ticksPaddingAbove, 1)
  )
  const stepSize =
    niceSteps.find(step => step >= roughStep) || niceSteps[niceSteps.length - 1]

  // Center the axis around the data when using expanded range
  const dataCenter = (minPrice + maxPrice) / 2
  const ticksForData = desiredTicks - ticksPaddingBelow - ticksPaddingAbove

  let yAxisMin =
    Math.floor((dataCenter - (ticksForData / 2) * stepSize) / stepSize) *
      stepSize -
    ticksPaddingBelow * stepSize
  if (yAxisMin < 0) yAxisMin = 0

  // Ensure min price is visible (at least ticksPaddingBelow steps below)
  const minPriceFloor =
    Math.floor(minPrice / stepSize) * stepSize - ticksPaddingBelow * stepSize
  if (yAxisMin > minPriceFloor) {
    yAxisMin = Math.max(minPriceFloor, 0)
  }

  const yAxisMax =
    Math.ceil(maxPrice / stepSize) * stepSize + ticksPaddingAbove * stepSize

  return {
    stepSize,
    yAxisMin,
    yAxisMax,
    ticksBelow: ticksPaddingBelow,
    ticksAbove: ticksPaddingAbove,
    desiredTicks
  }
}
